import { type ChangeEvent, type FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getMediaDisplayUrl, uploadFile } from "../../config/api";
import { uploadAccept, validateUploadFile } from "../../config/uploads";
import { getApiErrorMessage } from "../../lib/apiError";
import { QUANTITY_UNITS } from "../../lib/units";
import "./AddProductServiceModal.css";

export interface ProductServiceFormData {
  id?: string;
  name: string;
  description: string;
  type: "product" | "service";
  category: string;
  quantity: string;
  /** Canonical unit symbol (`src/lib/units.ts`); the price is per this unit. */
  quantityUnit?: string;
  price: string;
  currency: string;
  deliveryTime: string;
  region: string;
  images?: string[];
}

/** ISO 4217 codes, shown as-is in every language. */
const CURRENCY_CODES = ["USD", "EUR", "GBP", "AED", "SAR", "CAD"] as const;

interface AddProductServiceModalProps {
  isOpen: boolean;
  onClose: () => void;
  editData?: ProductServiceFormData | null;
  onSave?: (data: ProductServiceFormData) => void;
  /** When true, submit button shows loading (e.g. while update is in progress). */
  isSubmitting?: boolean;
  /** Error message to show when update fails. */
  submitError?: string | null;
}

const AddProductServiceModal = ({
  isOpen,
  onClose,
  editData = null,
  onSave,
  isSubmitting = false,
  submitError = null,
}: AddProductServiceModalProps) => {
  const { t } = useTranslation(["company", "common", "domain"]);
  const [type, setType] = useState<"product" | "service">("product");
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    category: "",
    quantity: "",
    quantityUnit: "",
    price: "",
    currency: "",
    deliveryTime: "",
    region: "",
  });
  const [images, setImages] = useState<string[]>([]);
  const [isUploadingImages, setIsUploadingImages] = useState(false);
  const [imageUploadError, setImageUploadError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    setImageUploadError(null);
    if (editData) {
      setType(editData.type || "product");
      setFormData({
        name: editData.name || "",
        description: editData.description || "",
        category: editData.category || "",
        quantity: editData.quantity || "",
        quantityUnit: editData.quantityUnit || "",
        price: editData.price || "",
        currency: editData.currency || "",
        deliveryTime: editData.deliveryTime || "",
        region: editData.region || "",
      });
      setImages(editData.images || []);
    } else {
      setType("product");
      setFormData({
        name: "",
        description: "",
        category: "",
        quantity: "",
        quantityUnit: "",
        price: "",
        currency: "",
        deliveryTime: "",
        region: "",
      });
      setImages([]);
    }
  }, [editData, isOpen]);

  if (!isOpen) return null;

  const handleInputChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length) return;
    setImageUploadError(null);
    setIsUploadingImages(true);
    try {
      const selected = Array.from(files) as File[];
      const rejected = selected.map((file) => validateUploadFile(file, "image")).filter((m): m is string => m !== null);
      const uploads = selected
        .filter((file) => validateUploadFile(file, "image") === null)
        .map(async (file: File): Promise<string> => (await uploadFile(file)).url);
      const urls = await Promise.all(uploads);
      if (rejected.length > 0) {
        setImageUploadError(
          rejected.length === 1 ? rejected[0] : t("addProductService.filesSkipped", { count: rejected.length }),
        );
      }
      setImages((prev) => [...prev, ...urls]);
    } catch (err) {
      setImageUploadError(getApiErrorMessage(err, t("addProductService.uploadFailed")));
    } finally {
      setIsUploadingImages(false);
    }
    e.target.value = "";
  };

  const handleRemoveImage = (index: number) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const data: ProductServiceFormData = {
      ...formData,
      type,
      images,
      ...(editData?.id && { id: editData.id }),
    };
    if (onSave) {
      onSave(data);
    }
    if (!editData?.id) onClose();
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">
            {editData?.id ? t("addProductService.editTitle") : t("addProductService.addTitle")}
          </h2>
          <button
            type="button"
            className="modal-close"
            onClick={onClose}
            aria-label={t("addProductService.closeModal")}
          >
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        <form className="modal-form" onSubmit={handleSubmit}>
          <div className="modal-form__type-selection">
            <label className="modal-form__type-label">{t("addProductService.type")}</label>
            <div className="modal-form__type-options">
              <button
                type="button"
                className={`modal-form__type-option ${type === "product" ? "modal-form__type-option--active" : ""}`}
                onClick={() => setType("product")}
              >
                <i className="fa-solid fa-cube" />
                <span>{t("addProductService.product")}</span>
              </button>
              <button
                type="button"
                className={`modal-form__type-option ${type === "service" ? "modal-form__type-option--active" : ""}`}
                onClick={() => setType("service")}
              >
                <i className="fa-solid fa-building" />
                <span>{t("addProductService.service")}</span>
              </button>
            </div>
          </div>

          <div className="modal-form__field">
            <label className="modal-form__label" htmlFor="name">
              {t("addProductService.name")}
            </label>
            <input
              id="name"
              name="name"
              type="text"
              className="modal-form__input"
              placeholder={t("addProductService.namePlaceholder")}
              value={formData.name}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="modal-form__field">
            <label className="modal-form__label" htmlFor="description">
              {t("addProductService.description")}
            </label>
            <textarea
              id="description"
              name="description"
              className="modal-form__textarea"
              placeholder={t("addProductService.descriptionPlaceholder")}
              rows={4}
              value={formData.description}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="modal-form__field">
            <label className="modal-form__label" htmlFor="category">
              {t("addProductService.category")}
            </label>
            <input
              id="category"
              name="category"
              type="text"
              className="modal-form__input"
              placeholder={t("addProductService.categoryPlaceholder")}
              value={formData.category}
              onChange={handleInputChange}
              required
            />
          </div>

          {type === "product" && (
            <div className="modal-form__field">
              <label className="modal-form__label" htmlFor="quantity">
                {t("addProductService.quantity")}
              </label>
              <input
                id="quantity"
                name="quantity"
                type="number"
                className="modal-form__input"
                placeholder={t("addProductService.quantityPlaceholder")}
                value={formData.quantity}
                onChange={handleInputChange}
                required
              />
              <label className="modal-form__label" htmlFor="quantityUnit">
                {t("addProductService.quantityUnit")}
              </label>
              <select
                id="quantityUnit"
                name="quantityUnit"
                className="modal-form__input"
                value={formData.quantityUnit}
                onChange={handleInputChange}
              >
                <option value="">{t("addProductService.quantityUnitNone")}</option>
                {QUANTITY_UNITS.map((u) => (
                  <option key={u.symbol} value={u.symbol}>
                    {t(`domain:unit.${u.key}`)}
                  </option>
                ))}
              </select>
              <p className="modal-form__hint">{t("addProductService.quantityUnitHint")}</p>
            </div>
          )}

          <div className="modal-form__row">
            <div className="modal-form__field modal-form__field--half">
              <label className="modal-form__label" htmlFor="price">
                {t("addProductService.price")}
              </label>
              <input
                id="price"
                name="price"
                type="number"
                step="0.01"
                className="modal-form__input"
                placeholder={t("addProductService.pricePlaceholder")}
                value={formData.price}
                onChange={handleInputChange}
                required
              />
            </div>

            <div className="modal-form__field modal-form__field--half">
              <label className="modal-form__label" htmlFor="currency">
                {t("addProductService.currency")}
              </label>
              <select
                id="currency"
                name="currency"
                className="modal-form__select"
                value={formData.currency}
                onChange={handleInputChange}
                required
              >
                <option value="">{t("addProductService.selectCurrency")}</option>
                {CURRENCY_CODES.map((code) => (
                  <option key={code} value={code}>
                    {code}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="modal-form__field">
            <label className="modal-form__label" htmlFor="deliveryTime">
              {t("addProductService.deliveryTime")}
            </label>
            <input
              id="deliveryTime"
              name="deliveryTime"
              type="text"
              className="modal-form__input"
              placeholder={t("addProductService.deliveryTimePlaceholder")}
              value={formData.deliveryTime}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="modal-form__field">
            <label className="modal-form__label" htmlFor="region">
              {t("addProductService.region")}
            </label>
            <input
              id="region"
              name="region"
              type="text"
              className="modal-form__input"
              placeholder={t("addProductService.regionPlaceholder")}
              value={formData.region}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="modal-form__field">
            <label className="modal-form__label">{t("addProductService.images")}</label>
            <div>
              <input
                type="file"
                id="image-upload"
                accept={uploadAccept("image")}
                multiple
                className="modal-form__file-input"
                onChange={handleFileUpload}
                disabled={isUploadingImages}
              />
              <label htmlFor="image-upload" className="modal-form__upload-button" aria-busy={isUploadingImages}>
                {isUploadingImages ? (
                  <>
                    <i className="fa-solid fa-spinner fa-spin" />
                    <span>{t("addProductService.uploading")}</span>
                  </>
                ) : (
                  <>
                    <i className="fa-solid fa-arrow-up-from-bracket" />
                    <span>{t("addProductService.uploadImages")}</span>
                  </>
                )}
              </label>
            </div>
            {imageUploadError && (
              <p className="modal-form__error" role="alert">
                {imageUploadError}
              </p>
            )}
            {images.length > 0 && (
              <div className="modal-form__images-preview">
                {images.map((image, index) => (
                  <div key={index} className="modal-form__image-thumbnail">
                    <img src={getMediaDisplayUrl(image)} alt={t("addProductService.imageAlt", { index: index + 1 })} />
                    <button
                      type="button"
                      className="modal-form__image-delete"
                      onClick={() => handleRemoveImage(index)}
                      aria-label={t("addProductService.removeImage")}
                    >
                      <i className="fa-solid fa-xmark" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {submitError && (
            <p className="modal-form__error" role="alert">
              {submitError}
            </p>
          )}
          <div className="modal-footer">
            <button
              type="button"
              className="modal-footer__button modal-footer__button--cancel"
              onClick={onClose}
              disabled={isSubmitting}
            >
              {t("common:actions.cancel")}
            </button>
            <button
              type="submit"
              className="modal-footer__button modal-footer__button--primary"
              disabled={isSubmitting}
            >
              {editData?.id
                ? isSubmitting
                  ? t("addProductService.updating")
                  : t("common:actions.update")
                : type === "service"
                  ? isSubmitting
                    ? t("addProductService.adding")
                    : t("addProductService.addService")
                  : t("addProductService.nextHsCode")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddProductServiceModal;
